import {
  TrendingUp, Sun, Battery, Car, Flame, Plus, FileText, ChevronRight,
  Zap, ShieldCheck, Sparkles, Clock, CheckCircle2, ArrowDownToLine,
  Plug, Wrench, AlertCircle,
} from 'lucide-react';

/**
 * Powersure — Zo ziet Mijn Powersure eruit
 *
 * P1 Flow-fix (Feb 2026): de mockups moeten het *maximale* gekoppelde
 * Powersure-resultaat tonen — niet de lege/voorbeeld-situatie.
 *   1. Dashboard — Live inzicht, P1 gekoppeld, zon/verbruik/teruglever
 *   2. Apparaten — verbonden apparaten met status (Gekoppeld / Sturing
 *      mogelijk / Alleen meten / Installateur nodig / Later uitbreiden)
 *   3. Advies & offertes — adviezen, offerte, "sturing alleen met OK"
 */

function MockPhoneFrame({ children, label, testId }) {
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative w-full max-w-[280px] sm:max-w-[300px]">
        <div className="rounded-[2.2rem] bg-black/40 border border-white/15 shadow-2xl p-2.5 backdrop-blur-sm">
          <div
            className="rounded-[1.8rem] overflow-hidden border border-white/10 aspect-[9/16.5] relative"
            style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}
          >
            <div className="absolute top-0 left-0 right-0 h-5 flex items-center justify-center z-10">
              <div className="w-14 h-1 bg-black/70 rounded-full" />
            </div>
            <div className="pt-6 pb-3 px-3 h-full overflow-hidden">{children}</div>
          </div>
        </div>
      </div>
      <p
        className="text-xs sm:text-sm text-white/65 text-center mt-4 max-w-[260px] leading-relaxed"
        style={{ fontFamily: 'Outfit' }}
      >
        {label}
      </p>
    </div>
  );
}

/* ──────────────── 1) DASHBOARD — volledig gekoppeld ───────────────── */
function DashboardMock() {
  return (
    <div className="space-y-2">
      {/* Header — Mijn Powersure + Live inzicht + P1 gekoppeld */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[9px] font-semibold text-emerald-300/90 tracking-[0.18em] uppercase">Mijn Powersure</div>
          <div className="text-[12px] font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Live inzicht actief</div>
        </div>
        <span className="inline-flex items-center gap-0.5 text-[7.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-400/30">
          <span className="w-1 h-1 rounded-full bg-emerald-300 animate-pulse" /> P1 gekoppeld
        </span>
      </div>

      {/* Live KPI strip — zon / verbruik / teruglever */}
      <div className="grid grid-cols-3 gap-1">
        <div className="rounded-md bg-amber-500/10 border border-amber-400/25 p-1.5">
          <div className="flex items-center gap-0.5 mb-0.5">
            <Sun className="w-2.5 h-2.5 text-amber-300" />
            <span className="text-[7.5px] font-semibold text-amber-200">Zon</span>
          </div>
          <div className="text-[10px] font-semibold text-white">3,4 kW</div>
        </div>
        <div className="rounded-md bg-sky-500/10 border border-sky-400/25 p-1.5">
          <div className="flex items-center gap-0.5 mb-0.5">
            <Zap className="w-2.5 h-2.5 text-sky-300" />
            <span className="text-[7.5px] font-semibold text-sky-200">Verbruik</span>
          </div>
          <div className="text-[10px] font-semibold text-white">1,5 kW</div>
        </div>
        <div className="rounded-md bg-emerald-500/10 border border-emerald-400/25 p-1.5">
          <div className="flex items-center gap-0.5 mb-0.5">
            <ArrowDownToLine className="w-2.5 h-2.5 text-emerald-300" />
            <span className="text-[7.5px] font-semibold text-emerald-200">Terug</span>
          </div>
          <div className="text-[10px] font-semibold text-white">1,9 kW</div>
        </div>
      </div>

      {/* Eigen Zonnestroom Score */}
      <div className="rounded-md bg-white/5 border border-white/10 p-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-white/65">Eigen zonnestroom</span>
          <span className="text-[10px] font-semibold text-emerald-300">72%</span>
        </div>
        <div className="h-0.5 bg-white/8 rounded-full mt-1 overflow-hidden">
          <div className="h-0.5 bg-emerald-400 rounded-full" style={{ width: '72%' }} />
        </div>
      </div>

      {/* Top 3 kansen */}
      <div className="text-[8.5px] font-semibold text-emerald-300/90 tracking-[0.18em] uppercase pt-0.5">Top 3 kansen</div>
      {[
        { t: 'Was-droger nachtprogramma', badge: 'Aanbevolen', Icon: Zap, tone: 'emerald' },
        { t: 'Omvormer-richting bijstellen', badge: 'Eerst meten', Icon: Sun, tone: 'sky' },
        { t: 'Boiler temperatuur omlaag', badge: 'Later plannen', Icon: Flame, tone: 'amber' },
      ].map((k, i) => {
        const badgeCls = {
          emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
          sky:     'bg-sky-500/15 text-sky-300 border-sky-400/30',
          amber:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
        }[k.tone];
        return (
          <div key={i} className="rounded-md bg-white/5 border border-white/10 p-1 flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-md bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
              <k.Icon className="w-2.5 h-2.5 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9.5px] text-white leading-tight truncate">{k.t}</div>
            </div>
            <span className={`text-[7px] uppercase tracking-wider font-medium px-1 py-[1px] rounded-full border whitespace-nowrap ${badgeCls}`}>
              {k.badge}
            </span>
          </div>
        );
      })}

      {/* Volgende stap */}
      <div className="rounded-md bg-emerald-500/10 border border-emerald-400/30 p-1.5 flex items-center gap-1.5">
        <Clock className="w-3 h-3 text-emerald-300 shrink-0" />
        <div className="text-[9px] text-white leading-tight">
          <span className="font-semibold text-emerald-200">Volgende stap:</span> vaatwasser 13:30
        </div>
      </div>
    </div>
  );
}

/* ─────────────── 2) APPARATEN — verbonden met status ──────────────── */
function DevicesMock() {
  const devices = [
    { l: 'P1-meter',     Icon: TrendingUp, status: 'Gekoppeld',         tone: 'emerald' },
    { l: 'Omvormer',     Icon: Sun,        status: 'Gekoppeld',         tone: 'emerald' },
    { l: 'Laadpaal',     Icon: Car,        status: 'Sturing mogelijk',  tone: 'emerald' },
    { l: 'Warmtepomp',   Icon: Flame,      status: 'Installateur',      tone: 'sky' },
    { l: 'Boiler',       Icon: Flame,      status: 'Alleen meten',      tone: 'slate' },
    { l: 'Batterij',     Icon: Battery,    status: 'Later uitbreiden',  tone: 'amber' },
    { l: 'Slimme stekker', Icon: Plug,     status: 'Gekoppeld',         tone: 'emerald' },
  ];
  const dotCls = {
    emerald: 'bg-emerald-300',
    sky:     'bg-sky-300',
    amber:   'bg-amber-300',
    slate:   'bg-white/55',
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[9px] font-semibold text-emerald-300/90 tracking-[0.18em] uppercase">Apparaten</div>
          <div className="text-[12px] font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>4 gekoppeld · 3 te activeren</div>
        </div>
        <span className="inline-flex items-center gap-0.5 text-[7.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-400/30">
          <CheckCircle2 className="w-2 h-2" /> Veilig
        </span>
      </div>

      {devices.map((d, i) => (
        <div key={i} className="rounded-md bg-white/5 border border-white/10 px-1.5 py-1 flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-md bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
            <d.Icon className="w-2.5 h-2.5 text-emerald-300" />
          </div>
          <span className="flex-1 text-[9.5px] text-white leading-tight truncate">{d.l}</span>
          <span className="inline-flex items-center gap-1 text-[8px] text-white/75">
            <span className={`w-1 h-1 rounded-full ${dotCls[d.tone]}`} />
            {d.status}
          </span>
        </div>
      ))}

      <button className="w-full rounded-md bg-emerald-500 text-emerald-950 text-[9.5px] font-semibold py-1 flex items-center justify-center gap-1">
        <Plus className="w-2.5 h-2.5" /> Apparaat toevoegen
      </button>
    </div>
  );
}

/* ───────────── 3) ADVIES & OFFERTES — vervolgstappen ──────────────── */
function QuotesMock() {
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] font-semibold text-emerald-300/90 tracking-[0.18em] uppercase">Advies & offertes</div>
      <div className="text-[12px] font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Uw volgende stap</div>

      {/* Advieskaart */}
      <div className="rounded-md bg-white/5 border border-white/10 p-1.5">
        <div className="flex items-start justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5 text-emerald-300" />
            <span className="text-[9.5px] font-semibold text-white leading-tight">
              Laad uw EV met zonnestroom
            </span>
          </div>
          <span className="text-[7px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-1 py-[1px] rounded-full font-medium whitespace-nowrap">
            Aanbevolen
          </span>
        </div>
        <p className="text-[8.5px] text-white/55 leading-snug">Plan tussen 12:00–15:00 — meer eigen stroom benut.</p>
      </div>

      {/* Offertekaart */}
      <div className="rounded-md bg-white/5 border border-white/10 p-1.5">
        <div className="flex items-center gap-1 mb-0.5">
          <FileText className="w-2.5 h-2.5 text-emerald-300" />
          <span className="text-[9.5px] font-semibold text-white">Warmtepomp WPK-8</span>
          <span className="ml-auto text-[7px] uppercase tracking-wider bg-sky-500/15 text-sky-300 border border-sky-400/30 px-1 py-[1px] rounded-full font-medium">
            Voorstel
          </span>
        </div>
        <p className="text-[8.5px] text-white/55 leading-snug mb-1">Inclusief installatie en garantie. 1 partner.</p>
        <div className="flex items-center justify-between">
          <span className="text-[8.5px] text-white/55 inline-flex items-center gap-0.5">
            <Wrench className="w-2 h-2" /> Installateur
          </span>
          <button className="text-[8.5px] bg-emerald-500 text-emerald-950 font-semibold px-1.5 py-[2px] rounded inline-flex items-center gap-0.5">
            Bekijk <ChevronRight className="w-2 h-2" />
          </button>
        </div>
      </div>

      {/* Vervolgstap */}
      <div className="rounded-md bg-white/5 border border-white/10 p-1.5 flex items-center gap-1.5">
        <Plug className="w-2.5 h-2.5 text-amber-300 shrink-0" />
        <span className="text-[8.5px] text-white/75 leading-tight">
          Sluit slimme stekker af — wacht op uw OK
        </span>
        <span className="ml-auto text-[7px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/30 px-1 py-[1px] rounded-full font-medium">
          Plannen
        </span>
      </div>

      {/* Veiligheids-statement */}
      <div className="rounded-md bg-emerald-500/10 border border-emerald-400/30 p-1.5 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3 text-emerald-300 shrink-0" />
        <span className="text-[8.5px] text-emerald-100 leading-tight">
          Sturing alleen met uw goedkeuring
        </span>
      </div>
    </div>
  );
}

export default function PowersureAppPreviewSection() {
  return (
    <section
      className="py-16 sm:py-20"
      style={{ background: 'linear-gradient(180deg, #0a1410 0%, #0e1b16 100%)' }}
      data-testid="powersure-app-preview-section"
    >
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
              Zo werkt Mijn Powersure
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white max-w-2xl mx-auto leading-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            Volledig gekoppeld — meting, apparaten en advies in één scherm.
          </h2>
          <p className="text-sm text-white/65 mt-3 max-w-xl mx-auto">
            Dit is hoe Mijn Powersure eruitziet zodra uw P1-meter, omvormer en
            apparaten gekoppeld zijn. Geen dashboards vol grafieken — alleen
            wat u nu kunt doen.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 sm:gap-6">
          <MockPhoneFrame label="Dashboard — live inzicht, P1 gekoppeld, top 3 kansen." testId="app-preview-dashboard">
            <DashboardMock />
          </MockPhoneFrame>
          <MockPhoneFrame label="Apparaten — P1, omvormer, laadpaal, warmtepomp, boiler, batterij en stekkers." testId="app-preview-devices">
            <DevicesMock />
          </MockPhoneFrame>
          <MockPhoneFrame label="Advies & offertes — duidelijke vervolgstap, sturing alleen met uw OK." testId="app-preview-quotes">
            <QuotesMock />
          </MockPhoneFrame>
        </div>

        {/* P1/meetbron uitleg — visueel naast de mockups */}
        <div
          className="mt-10 sm:mt-14 max-w-3xl mx-auto rounded-2xl border border-emerald-400/30 p-5 sm:p-6"
          style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="app-preview-measure-note"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0 mt-0.5">
              <TrendingUp className="w-4 h-4" />
            </span>
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-300/90 mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                Meetbron · P1
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
                Voor echt live inzicht heeft u een meetbron nodig
              </h3>
              <p className="text-sm text-white/75 leading-relaxed mt-2">
                Voor live inzicht koppelt u een meetbron, meestal een{' '}
                <strong className="text-white">P1-meter of slimme meter</strong>.
                Zonder koppeling kunt u alvast zien hoe Mijn Powersure werkt.
                Met koppeling worden inzichten persoonlijker en actueler.
              </p>
              <p className="text-xs sm:text-sm text-emerald-300/90 mt-2 leading-relaxed">
                Eerst meten en begrijpen. Daarna advies. Automatische sturing
                alleen waar ondersteund en met uw goedkeuring.
              </p>
            </div>
          </div>
        </div>

        {/* Trust bar — onderaan, 4 pillars */}
        <div className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto" data-testid="app-preview-trustbar">
          {[
            { icon: ShieldCheck, t: 'Veilig & betrouwbaar' },
            { icon: TrendingUp,  t: 'Meer eigen stroom' },
            { icon: Clock,       t: 'Tijd besparen' },
            { icon: AlertCircle, t: 'Eerst meten, dan advies' },
          ].map((p, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 shrink-0">
                <p.icon className="w-3.5 h-3.5" />
              </span>
              <span className="text-xs sm:text-sm text-white/85 font-medium">{p.t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
